import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const buildRedirect = (pathname: string, search: string, hash: string) =>
  `${pathname}${search}${hash}`;

const LoginRedirect = ({ target }: { target: string }) => (
  <Navigate to={`/login?redirect=${encodeURIComponent(target)}`} replace />
);

const InternalPolicyRedirect = ({ target }: { target: string }) => (
  <Navigate to={`/internal/gdpr-approval?redirect=${encodeURIComponent(target)}`} replace />
);

const LoadingState = () => (
  <div className="min-h-screen admin-shell flex items-center justify-center">
    <p className="text-sm text-muted-foreground">Betöltés...</p>
  </div>
);

export const RequireAuth = ({
  children,
  skipInternalPolicyCheck = false,
}: {
  children: ReactNode;
  skipInternalPolicyCheck?: boolean;
}) => {
  const {
    user,
    loading,
    internalPolicyAccepted,
    internalPolicyLoading,
  } = useAuth();
  const location = useLocation();

  if (loading || (!skipInternalPolicyCheck && internalPolicyLoading)) return <LoadingState />;
  if (!user) {
    const target = buildRedirect(location.pathname, location.search, location.hash);
    return <LoginRedirect target={target} />;
  }
  if (!skipInternalPolicyCheck && !internalPolicyAccepted) {
    const target = buildRedirect(location.pathname, location.search, location.hash);
    return <InternalPolicyRedirect target={target} />;
  }

  return <>{children}</>;
};

export const RequireAdmin = ({ children }: { children: ReactNode }) => {
  const { user, isAdmin, loading, internalPolicyAccepted, internalPolicyLoading } = useAuth();
  const location = useLocation();

  if (loading || internalPolicyLoading) return <LoadingState />;
  if (!user) {
    const target = buildRedirect(location.pathname, location.search, location.hash);
    return <LoginRedirect target={target} />;
  }
  if (!internalPolicyAccepted) {
    const target = buildRedirect(location.pathname, location.search, location.hash);
    return <InternalPolicyRedirect target={target} />;
  }
  if (!isAdmin) return <Navigate to="/staff" replace />;

  return <>{children}</>;
};
